import { useSelector } from "react-redux";
import { applyFilters as applyHardcodedFilters } from '../../../store/slices/featuresSlice';
import styles from './DropdownFilter.module.css';

/**
 * DropdownFilter - A reusable dropdown filter component
 * 
 * @param {Function} handleSelect - Callback when selection changes
 * @param {string|number|Array} seriesIds - Series ID(s) to get data from
 * @param {string} label - Label for the "All" option (e.g., "Colors" -> "All Colors")
 * @param {string} value - Currently selected value
 * @param {string} column - Product attribute to extract unique values from
 * @param {Object} filterBy - Optional filter to apply before extracting options
 * @param {Array} hardcodedFilters - Array of filter objects with { column, value, operator } for advanced filtering
 * @param {string} className - Additional CSS class
 * @param {Array} data - Optional direct data array (bypasses Redux lookup)
 */
export default function DropdownFilter({ 
  handleSelect, 
  seriesIds, 
  label, 
  value, 
  column, 
  filterBy,
  hardcodedFilters = [],
  className,
  data: directData 
}) {

  // Get data from Redux state (shared.seriesData)
  const reduxData = useSelector((state) => {
    if (directData) return []; // Skip Redux if direct data provided
    
    const ids = Array.isArray(seriesIds) ? seriesIds : [seriesIds];
    // Combine all products from all series into a single array
    return ids.flatMap((id) => {
      const seriesData = state.shared?.seriesData?.[id];
      return seriesData ? Object.values(seriesData) : [];
    });
  }) || [];

  // Use direct data if provided, otherwise use Redux data
  const data = directData || reduxData;
  
  // Apply hardcoded filters first (with operator support)
  let filteredData = data.filter(item => applyHardcodedFilters(item, hardcodedFilters));
  
  // Apply filterBy if provided
  
  // Apply filterBy if provided
  if (filterBy) {
    filteredData = filteredData.filter(item => {
      return Object.entries(filterBy).every(([key, val]) => {
        // Skip if filter value is -1 (All)
        if (val === -1 || val === '-1') return true;
        
        const itemValue = item[key];
        if (!itemValue) return false;
        
        // Handle comma-separated values - check if any value matches
        const itemValues = String(itemValue).split(',').map(v => v.trim());
        return itemValues.includes(String(val).trim()) || itemValue === val;
      });
    });
  }
  
  // Ensure unique dropdown options and sort alphabetically
  // Handle comma-separated values by splitting them
  const allValues = filteredData.flatMap(d => {
    const val = d[column];
    if (!val) return [];
    // Split by comma and trim whitespace
    return String(val).split(',').map(v => v.trim()).filter(Boolean);
  });
  
  const uniqueOptions = [...new Set(allValues)].sort((a, b) => 
    String(a).localeCompare(String(b))
  );

  // Don't render if there are no options
  if (uniqueOptions.length === 0) {
    return null;
  }

  const dropdownOptions = uniqueOptions.map((option, index) => (
    <option key={index} value={option}>
      {option}
    </option>
  ));

  return (
    <select
      name={column}
      className={`${styles.dropdown} ${className || ''}`}
      value={value}
      onChange={handleSelect}
    >
      <option value="-1">All {label}</option>
      {dropdownOptions}
    </select>
  );
}
